/**
 * FB2 (FictionBook 2.0) Parser
 * Parses FB2 XML format and extracts book metadata and content
 */

import { DOMParser as NodeDOMParser } from '@xmldom/xmldom';

// Use xmldom DOMParser in Node.js environment, native DOMParser in browser
const getDOMParser = (): typeof DOMParser => {
  if (typeof DOMParser !== 'undefined') {
    return DOMParser;
  }
  return NodeDOMParser as any;
};

// Helper to get first element by tag name (works with both DOM APIs)
const getElement = (parent: Document | Element, tagName: string): Element | null => {
  const elements = parent.getElementsByTagName(tagName);
  return elements.length > 0 ? (elements[0] ?? null) : null;
};

// Helper to get all elements by tag name
const getElements = (parent: Document | Element, tagName: string): Element[] => {
  return Array.from(parent.getElementsByTagName(tagName));
};

export interface FB2Metadata {
  title: string;
  author?: string;
  genre?: string[];
  lang?: string;
  date?: string;
  coverImage?: string;
}

export interface FB2Image {
  id: string;
  contentType: string;
  data: string; // base64
}

export interface FB2Section {
  title?: string;
  content: string[];
  subsections: FB2Section[];
}

export interface FB2Book {
  metadata: FB2Metadata;
  sections: FB2Section[];
  images: Map<string, FB2Image>;
}

export class FB2Parser {
  parseXML(xmlContent: string): FB2Book {
    const ParserClass = getDOMParser();
    const parser = new ParserClass();
    const doc = parser.parseFromString(xmlContent, 'text/xml');

    // Check for parsing errors
    const parserError = doc.getElementsByTagName('parsererror')[0];
    if (parserError) {
      throw new Error('Invalid FB2 XML: ' + parserError.textContent);
    }

    const metadata = this.extractMetadata(doc);
    const images = this.extractImages(doc);
    const sections = this.extractSections(doc);

    return { metadata, sections, images };
  }

  private extractMetadata(doc: Document): FB2Metadata {
    const titleInfo = getElement(doc, 'title-info');
    if (!titleInfo) {
      throw new Error('Missing title-info in FB2 file');
    }

    const title = getElement(titleInfo, 'book-title')?.textContent?.trim() || 'Untitled';

    const author = titleInfo.getElementsByTagName('author')[0];
    const authorFirstName = author ? getElement(author, 'first-name')?.textContent?.trim() : undefined;
    const authorLastName = author ? getElement(author, 'last-name')?.textContent?.trim() : undefined;
    const authorName = [authorFirstName, authorLastName].filter(Boolean).join(' ') || undefined;

    const genres = getElements(titleInfo, 'genre')
      .map(g => g.textContent?.trim())
      .filter(Boolean) as string[];

    const lang = getElement(titleInfo, 'lang')?.textContent?.trim();
    const date = getElement(titleInfo, 'date')?.textContent?.trim();

    // Extract cover image reference
    const coverpage = getElement(titleInfo, 'coverpage');
    const coverImageEl = coverpage ? getElement(coverpage, 'image') : null;
    const coverImageRef = coverImageEl?.getAttribute('l:href') || coverImageEl?.getAttribute('xlink:href');
    const coverImage = coverImageRef?.replace(/^#/, '');

    return {
      title,
      ...(authorName !== undefined && { author: authorName }),
      ...(genres.length > 0 && { genre: genres }),
      ...(lang !== undefined && { lang }),
      ...(date !== undefined && { date }),
      ...(coverImage !== undefined && { coverImage })
    };
  }

  private extractImages(doc: Document): Map<string, FB2Image> {
    const images = new Map<string, FB2Image>();
    const binaryElements = getElements(doc, 'binary');

    for (const binary of binaryElements) {
      const id = binary.getAttribute('id');
      const contentType = binary.getAttribute('content-type') || 'image/jpeg';
      const data = binary.textContent?.trim() || '';

      if (id && data) {
        images.set(id, { id, contentType, data });
      }
    }

    return images;
  }

  private extractSections(doc: Document): FB2Section[] {
    const body = getElement(doc, 'body');
    if (!body) {
      throw new Error('Missing body in FB2 file');
    }

    const sections: FB2Section[] = [];
    // Get direct child sections
    const bodySections = getElements(body, 'section');

    for (const sectionEl of bodySections) {
      sections.push(this.parseSection(sectionEl));
    }

    return sections;
  }

  private parseSection(sectionEl: Element): FB2Section {
    const section: Partial<FB2Section> & { content: string[]; subsections: FB2Section[] } = {
      content: [],
      subsections: []
    };

    // Extract title
    const titleEl = getElement(sectionEl, 'title');
    if (titleEl) {
      section.title = this.extractTextContent(titleEl);
    }

    // Extract content paragraphs
    const children = Array.from(sectionEl.childNodes).filter(
      node => node.nodeType === 1  // Element nodes only
    ) as Element[];
    for (const child of children) {
      if (child.tagName.toLowerCase() === 'section') {
        // Nested section
        section.subsections.push(this.parseSection(child));
      } else if (child.tagName.toLowerCase() === 'p') {
        // Paragraph
        section.content.push(this.extractTextContent(child));
      } else if (child.tagName.toLowerCase() === 'poem') {
        // Poem
        section.content.push(this.extractPoem(child));
      } else if (child.tagName.toLowerCase() === 'epigraph') {
        // Epigraph
        section.content.push(this.extractEpigraph(child));
      } else if (child.tagName.toLowerCase() === 'cite') {
        // Citation
        section.content.push(this.extractCite(child));
      } else if (child.tagName.toLowerCase() === 'empty-line') {
        // Empty line
        section.content.push('');
      } else if (child.tagName.toLowerCase() === 'image') {
        // Image reference
        const href = child.getAttribute('l:href') || child.getAttribute('xlink:href');
        if (href) {
          // Remove leading # from href (FB2 internal references)
          const imageId = href.replace(/^#/, '');
          section.content.push(`![Image](#${imageId})`);
        }
      }
    }

    return section;
  }

  private extractTextContent(element: Element): string {
    let text = '';

    for (const node of Array.from(element.childNodes)) {
      if (node.nodeType === 3) { // TEXT_NODE
        text += node.textContent;
      } else if (node.nodeType === 1) { // ELEMENT_NODE
        const el = node as Element;
        const tag = el.tagName.toLowerCase();

        if (tag === 'strong') {
          // Recursively process content to handle nested tags
          text += `**${this.extractTextContent(el)}**`;
        } else if (tag === 'emphasis') {
          text += `*${this.extractTextContent(el)}*`;
        } else if (tag === 'strikethrough') {
          text += `~~${this.extractTextContent(el)}~~`;
        } else if (tag === 'code') {
          text += `\`${el.textContent}\``;
        } else if (tag === 'a') {
          const href = el.getAttribute('l:href') || el.getAttribute('xlink:href') || '';
          text += `[${this.extractTextContent(el)}](${href})`;
        } else if (tag === 'image') {
          const href = el.getAttribute('l:href') || el.getAttribute('xlink:href') || '';
          // Remove leading # from href for FB2 internal references
          const imageId = href.replace(/^#/, '');
          text += `![](#${imageId})`;
        } else if (tag === 'sub') {
          // Subscript - use HTML tag (Markdown doesn't have native subscript)
          text += `<sub>${this.extractTextContent(el)}</sub>`;
        } else if (tag === 'sup') {
          // Superscript
          text += `<sup>${this.extractTextContent(el)}</sup>`;
        } else {
          // Unknown tag - recursively extract content
          text += this.extractTextContent(el);
        }
      }
    }

    return text.trim();
  }

  private extractPoem(poemEl: Element): string {
    const lines: string[] = [];
    const stanzas = getElements(poemEl, 'stanza');

    for (const stanza of stanzas) {
      const verses = getElements(stanza, 'v');
      for (const verse of verses) {
        lines.push('> ' + this.extractTextContent(verse));
      }
      lines.push('>'); // Empty line between stanzas
    }

    return lines.join('\n');
  }

  private extractEpigraph(epigraphEl: Element): string {
    const lines: string[] = [];
    const paragraphs = getElements(epigraphEl, 'p');

    for (const p of paragraphs) {
      lines.push('> ' + this.extractTextContent(p));
    }

    return lines.join('\n');
  }

  private extractCite(citeEl: Element): string {
    const lines: string[] = [];
    const paragraphs = getElements(citeEl, 'p');

    for (const p of paragraphs) {
      lines.push('> ' + this.extractTextContent(p));
    }

    return lines.join('\n');
  }
}
